import type {
  ArchetypeId,
  AxisId,
  SelvesInput,
  SelvesState,
} from "./types";
import {
  ARCHETYPE_ORDER,
  AXIS_ORDER,
} from "./types";
import { encodeState, decodeState } from "./codec";
import { ARCHETYPE_PRESETS, matchesArchetypePredicate } from "./universes";
import { generateUniverse } from "@/lib/universe";

/**
 * Standard 32-bit FNV-1a hash algorithm for deterministic seeding from strings.
 */
export function fnv1a(str: string): number {
  let hash = 2166136261;
  for (let i = 0; i < str.length; i++) {
    hash ^= str.charCodeAt(i);
    hash = Math.imul(hash, 16777619);
  }
  return hash >>> 0;
}

/**
 * Mulberry32 PRNG returning [0, 1).
 */
export function mulberry32(seed: number): () => number {
  let s = seed >>> 0;
  return () => {
    s = (s + 0x6d2b79f5) >>> 0;
    let t = Math.imul(s ^ (s >>> 15), 1 | s);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

/**
 * Archetype baseline bias vectors (0..100).
 * Carefully designed with large pairwise differences so that even before post-adjustment,
 * most axis pairs already differ substantially.
 */
const ARCHETYPE_BASE_AXES: Record<ArchetypeId, Record<AxisId, number>> = {
  // divergent: warm, human, grounded, stable community, moderate ambition
  divergent: {
    curiosity: 45,
    connection: 88,
    courage: 42,
    stability: 85,
    influence: 38,
  },
  // ascendant: cosmic scale, systemic reach, deep study, high influence & curiosity
  ascendant: {
    curiosity: 92,
    connection: 40,
    courage: 72,
    stability: 68,
    influence: 90,
  },
  // strange: high courage, extreme curiosity about alien physics, low conventional stability
  strange: {
    curiosity: 86,
    connection: 32,
    courage: 92,
    stability: 28,
    influence: 48,
  },
  // unexpected: high courage, surprising connection, spontaneous, inverted stability
  unexpected: {
    curiosity: 62,
    connection: 78,
    courage: 82,
    stability: 34,
    influence: 70,
  },
};

/**
 * Compute raw personality axis scores per archetype given the visitor's input and variant seed.
 * Jitter ranges up to +-10 using seed so different variants noticeably change axes.
 */
function computeRawAxesForArchetype(
  archetype: ArchetypeId,
  input: SelvesInput,
  seed: number
): Record<AxisId, number> {
  const rng = mulberry32(seed);
  const base = { ...ARCHETYPE_BASE_AXES[archetype] };

  // Apply shifts from user's selected strengths (if any)
  const strengthShifts: Record<AxisId, number> = {
    curiosity: 0,
    connection: 0,
    courage: 0,
    stability: 0,
    influence: 0,
  };

  for (const str of input.strengths) {
    switch (str) {
      case "curiosity":
        strengthShifts.curiosity += 5;
        break;
      case "empathy":
        strengthShifts.connection += 5;
        break;
      case "discipline":
        strengthShifts.stability += 5;
        break;
      case "creativity":
        strengthShifts.curiosity += 3;
        strengthShifts.courage += 3;
        break;
      case "courage":
        strengthShifts.courage += 6;
        break;
      case "logic":
        strengthShifts.stability += 4;
        strengthShifts.curiosity += 2;
        break;
      case "humor":
        strengthShifts.connection += 4;
        break;
      case "patience":
        strengthShifts.stability += 5;
        break;
    }
  }

  // Apply shifts from ambition
  if (input.ambition) {
    switch (input.ambition) {
      case "understand":
        strengthShifts.curiosity += 6;
        break;
      case "build":
        strengthShifts.stability += 4;
        strengthShifts.influence += 4;
        break;
      case "help":
        strengthShifts.connection += 6;
        break;
      case "lead":
        strengthShifts.influence += 7;
        strengthShifts.courage += 3;
        break;
      case "create":
        strengthShifts.curiosity += 4;
        strengthShifts.courage += 4;
        break;
      case "wander":
        strengthShifts.courage += 6;
        strengthShifts.stability -= 5;
        break;
    }
  }

  // Jitter based on seed: -10 to +10 so variants noticeably alter axes
  const result: Record<AxisId, number> = {
    curiosity: 50,
    connection: 50,
    courage: 50,
    stability: 50,
    influence: 50,
  };

  for (const axis of AXIS_ORDER) {
    const jitter = Math.floor((rng() - 0.5) * 20); // -10..+10
    const raw = base[axis] + strengthShifts[axis] + jitter;
    result[axis] = Math.max(5, Math.min(95, Math.round(raw)));
  }

  return result;
}

/**
 * Enforce the spec's axis invariant:
 * - For EVERY pair of selves (6 pairs among the 4), at least 3 of 5 axes differ by >= 15 points.
 * - No axis value is identical across all four selves.
 * - All axis values strictly clamped to integer [0..100].
 */
export function computeAllAxes(
  input: SelvesInput,
  variants: [number, number, number, number],
  baseSeed: number
): Record<ArchetypeId, Record<AxisId, number>> {
  const axesMap: Record<ArchetypeId, Record<AxisId, number>> = {
    divergent: computeRawAxesForArchetype("divergent", input, (baseSeed ^ 0x11111111) + variants[0]),
    ascendant: computeRawAxesForArchetype("ascendant", input, (baseSeed ^ 0x22222222) + variants[1]),
    strange: computeRawAxesForArchetype("strange", input, (baseSeed ^ 0x33333333) + variants[2]),
    unexpected: computeRawAxesForArchetype("unexpected", input, (baseSeed ^ 0x44444444) + variants[3]),
  };

  const archetypePriorityPairs: [ArchetypeId, ArchetypeId][] = [
    ["divergent", "ascendant"],
    ["divergent", "strange"],
    ["divergent", "unexpected"],
    ["ascendant", "strange"],
    ["ascendant", "unexpected"],
    ["strange", "unexpected"],
  ];

  for (let pass = 0; pass < 8; pass++) {
    for (const [idA, idB] of archetypePriorityPairs) {
      const a = axesMap[idA];
      const b = axesMap[idB];

      const deltas = AXIS_ORDER.map((axis) => Math.abs(a[axis] - b[axis]));
      const count15 = deltas.filter((d) => d >= 15).length;

      if (count15 < 3) {
        // Need to push apart axes that are closest or archetypally divergent
        for (const axis of AXIS_ORDER) {
          if (Math.abs(a[axis] - b[axis]) < 15) {
            // Push towards archetype base
            if (ARCHETYPE_BASE_AXES[idA][axis] >= ARCHETYPE_BASE_AXES[idB][axis]) {
              a[axis] = Math.min(100, a[axis] + 8);
              b[axis] = Math.max(0, b[axis] - 8);
            } else {
              a[axis] = Math.max(0, a[axis] - 8);
              b[axis] = Math.min(100, b[axis] + 8);
            }
          }
          const newCount = AXIS_ORDER.filter((ax) => Math.abs(a[ax] - b[ax]) >= 15).length;
          if (newCount >= 3) break;
        }
      }
    }
  }

  // Ensure no axis is identical across all four
  for (const axis of AXIS_ORDER) {
    const v0 = axesMap.divergent[axis];
    const v1 = axesMap.ascendant[axis];
    const v2 = axesMap.strange[axis];
    const v3 = axesMap.unexpected[axis];

    if (v0 === v1 && v1 === v2 && v2 === v3) {
      axesMap.divergent[axis] = Math.max(0, axesMap.divergent[axis] - 4);
      axesMap.ascendant[axis] = Math.min(100, axesMap.ascendant[axis] + 4);
    }
  }

  // Final round & clamp
  for (const arch of ARCHETYPE_ORDER) {
    for (const axis of AXIS_ORDER) {
      axesMap[arch][axis] = Math.max(0, Math.min(100, Math.round(axesMap[arch][axis])));
    }
  }

  return axesMap;
}

/**
 * Self-check helper checking key invariants across test states.
 * Verifies:
 * - Round-trip of encodeState / decodeState
 * - Presets strictly matching archetype predicates
 * - Axis rule satisfaction (>=3 axes differ by >= 15 for all 6 pairs)
 * - No identical axis across all 4 selves
 * - Mulberry32 determinism
 */
export function selfCheck(): string[] {
  const violations: string[] = [];

  // 1. Check presets against predicates
  for (const arch of ARCHETYPE_ORDER) {
    const cfg = ARCHETYPE_PRESETS[arch];
    const prof = generateUniverse(cfg);
    if (!matchesArchetypePredicate(arch, cfg, prof)) {
      violations.push(`Preset for archetype ${arch} does not satisfy predicate.`);
    }
  }

  // 2. Check 25 test states for axis invariants & codec round-trip with wider variants 0..999999
  for (let i = 0; i < 25; i++) {
    const testState: SelvesState = {
      input: {
        nickname: i % 2 === 0 ? `Explorer ${i}` : "",
        interests: i % 3 === 0 ? ["science", "technology"] : ["art", "nature", "stories"],
        strengths: i % 2 === 0 ? ["curiosity", "logic"] : ["empathy", "courage"],
        ambition: i % 4 === 0 ? "understand" : "build",
        lifePath: i % 3 === 0 ? "voyager" : "scholar",
      },
      variants: [
        (i * 37321) % 1_000_000,
        (i * 73491) % 1_000_000,
        (i * 109831) % 1_000_000,
        (i * 151249) % 1_000_000,
      ],
      universe: i === 5 ? ARCHETYPE_PRESETS.strange : null,
    };

    // Codec check
    const encoded = encodeState(testState);
    const decoded = decodeState(encoded);
    if (!decoded) {
      violations.push(`decodeState failed on state #${i}`);
    } else {
      const reEncoded = encodeState(decoded);
      if (reEncoded !== encoded) {
        violations.push(`Round-trip mismatch on state #${i}`);
      }
    }

    // Axes check
    const baseSeed = fnv1a(`test-check-${i}`);
    const axesMap = computeAllAxes(testState.input, testState.variants, baseSeed);

    // Pairwise differences
    const pairs: [ArchetypeId, ArchetypeId][] = [
      ["divergent", "ascendant"],
      ["divergent", "strange"],
      ["divergent", "unexpected"],
      ["ascendant", "strange"],
      ["ascendant", "unexpected"],
      ["strange", "unexpected"],
    ];

    for (const [idA, idB] of pairs) {
      const a = axesMap[idA];
      const b = axesMap[idB];
      let diff15 = 0;
      for (const axis of AXIS_ORDER) {
        if (Math.abs(a[axis] - b[axis]) >= 15) {
          diff15++;
        }
      }
      if (diff15 < 3) {
        violations.push(`Axis invariant violated between ${idA} and ${idB} in test state #${i}: only ${diff15} axes differ by >= 15`);
      }
    }

    // No all-four identical
    for (const axis of AXIS_ORDER) {
      const v0 = axesMap.divergent[axis];
      const v1 = axesMap.ascendant[axis];
      const v2 = axesMap.strange[axis];
      const v3 = axesMap.unexpected[axis];
      if (v0 === v1 && v1 === v2 && v2 === v3) {
        violations.push(`Axis ${axis} is identical across all four selves in test state #${i}`);
      }
    }
  }

  return violations;
}
